import React, { useState } from 'react';
import { MessageSquare, Phone, Mail, RefreshCw, Plus, Calendar } from 'lucide-react';
import { Interaction } from '../../types/lead';
import { BookingModal } from '../booking/BookingModal';
interface LeadTimelineProps {
  interactions: Interaction[];
  leadId: string;
  leadName: string;
  decisorEmail?: string;
  onAddNote?: (content: string) => void;
}
export function LeadTimeline({
  interactions,
  leadId,
  leadName,
  decisorEmail,
  onAddNote
}: LeadTimelineProps) {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const handleAddNote = () => {
    if (noteContent.trim() && onAddNote) {
      onAddNote(noteContent.trim());
      setNoteContent('');
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddNote();
    }
  };
  const getIcon = (type: Interaction['type']) => {
    switch (type) {
      case 'WA':
        return <MessageSquare className="w-4 h-4 text-growthly-white" data-id="element-573" />;
      case 'Call':
        return <Phone className="w-4 h-4 text-growthly-white" data-id="element-574" />;
      case 'Email':
        return <Mail className="w-4 h-4 text-growthly-white" data-id="element-575" />;
      case 'StateChange':
        return <RefreshCw className="w-4 h-4 text-growthly-white" data-id="element-576" />;
      default:
        return <MessageSquare className="w-4 h-4 text-growthly-white" data-id="element-577" />;
    }
  };
  const getColor = (type: Interaction['type']) => {
    switch (type) {
      case 'WA':
        return 'bg-green-500';
      case 'Call':
        return 'bg-blue-500';
      case 'Email':
        return 'bg-purple-500';
      case 'StateChange':
        return 'bg-growthly-black';
      default:
        return 'bg-growthly-medium';
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="card p-5 h-full flex flex-col" data-id="element-578">
      <div className="mb-6" data-id="element-579">
        <h3 className="text-[14px] font-semibold text-growthly-text mb-3" data-id="element-580">
          Próxima Acción
        </h3>
        <div className="bg-growthly-panel border border-white/5 rounded-md p-3 flex items-start" data-id="element-581">
          <Calendar className="w-5 h-5 text-growthly-text mr-3 flex-shrink-0 mt-0.5" data-id="element-582" />
          <div data-id="element-583">
            <p className="text-[13px] font-medium text-growthly-text" data-id="element-584">
              Enviar Propuesta Visual
            </p>
            <p className="text-[11px] text-growthly-muted mt-1" data-id="element-585">
              Pendiente de agendar
            </p>
            <div className="mt-3 flex space-x-2" data-id="element-586">
              <button onClick={() => setIsBookingOpen(true)} className="text-[11px] font-medium text-growthly-bg bg-growthly-text rounded px-3 py-1.5 hover:bg-growthly-text-2 transition-colors" data-id="element-587">
                Agendar
              </button>
              <button className="text-[11px] font-medium text-growthly-text border border-white/20 rounded px-3 py-1.5 hover:bg-white/10 transition-colors" data-id="element-588">
                Completar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4" data-id="element-589">
        <h3 className="text-sm font-semibold text-growthly-text" data-id="element-590">Historial</h3>
        <button className="text-xs font-medium text-growthly-text hover:underline flex items-center" data-id="element-591">
          <Plus className="w-3 h-3 mr-1" data-id="element-592" /> Nota
        </button>
      </div>

      <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar" data-id="element-593">
        <div className="relative border-l border-white/10 ml-3 space-y-6 pb-4" data-id="element-594">
          {interactions.map(interaction => <div key={interaction.id} className="relative pl-6" data-id="element-595">
              <div className={`absolute -left-3 top-0 w-6 h-6 rounded-full flex items-center justify-center ${getColor(interaction.type)} ring-4 ring-growthly-surface`} data-id="element-596">
                {getIcon(interaction.type)}
              </div>
              <div data-id="element-597">
                <div className="flex items-center justify-between mb-1" data-id="element-598">
                  <span className="text-xs font-medium text-growthly-text" data-id="element-599">
                    {interaction.type === 'StateChange' ? 'Cambio de estado' : interaction.type}
                  </span>
                  <span className="text-[10px] text-growthly-muted" data-id="element-600">
                    {formatDate(interaction.timestamp)}
                  </span>
                </div>
                <div className="text-sm text-growthly-muted bg-white/5 p-2 rounded border border-white/5" data-id="element-601">
                  {interaction.content}
                </div>
              </div>
            </div>)}
        </div>
      </div>

      <div className="pt-4 mt-auto border-t border-white/10" data-id="element-602">
        <div className="relative" data-id="element-603">
          <input type="text" value={noteContent} onChange={e => setNoteContent(e.target.value)} onKeyDown={handleKeyDown} placeholder="Añadir nota rápida... (Enter para guardar)" className="w-full text-sm border border-white/10 bg-growthly-bg text-growthly-text rounded-md pl-3 pr-10 py-2 focus:ring-1 focus:ring-growthly-amber focus:border-growthly-amber placeholder-growthly-muted" data-id="element-604" />
          <button onClick={handleAddNote} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-growthly-muted hover:text-growthly-text" data-id="element-605">
            <Plus className="w-4 h-4" data-id="element-606" />
          </button>
        </div>
      </div>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} leadId={leadId} leadName={leadName} decisorEmail={decisorEmail} data-id="element-607" />
    </div>;
}